<template>
    <div class="tickets-jira-view">
        <cadastro-toolbar
            :title="title"
            :token="token"
            :items="[]"
            :load-items-for-export="loadItemsForExport"
            :export-total="exportTotal"
            :csv-title="csvExportTitle"
            hide-search
        >
            <template #extension>
                <tickets-view-toolbar
                    :view-mode.sync="viewMode"
                    :filter-model="filters"
                    :opcoes="opcoesFiltro"
                    :status-list="statusList"
                    :client-list="clientFilterOptions"
                    :team-list="equipeList"
                    :default-team-ids="defaultTeamIds"
                    :show-client-filter="clientFilterOptions.length > 0"
                    :show-team-filter="equipeList.length > 0"
                    :show-new-ticket="false"
                    :show-title="false"
                    @update:filters="onToolbarFiltersUpdate"
                />
            </template>
            <template #add>
                <v-btn
                    v-if="showNewTicket"
                    color="primary"
                    dark
                    small
                    class="mb-2"
                    fab
                    @click="$emit('new-ticket')"
                >
                    <v-icon>mdi-plus</v-icon>
                </v-btn>
            </template>
        </cadastro-toolbar>
        <div class="board-list-area mt-2">
            <tickets-board
                v-if="viewMode === 'board'"
                :tickets-por-status="ticketsPorStatus"
                :totais-por-status="totaisPorStatus"
                :carregando-coluna="carregandoColuna"
                :status-list="statusList"
                :ocultar-pendente-vazio="!podeVerPendentes"
                :loading="loading"
                :column-cards-inverted="boardColumnCardsInverted"
                :status-drag-enabled="boardStatusDragEnabled"
                :show-print="showPrintButton"
                @update:columnCardsInverted="onColumnCardsInvertedUpdate"
                @carregar-mais="carregarMaisColuna"
                @card-click="openDetail($event)"
                @print-ticket="openPrint"
                @ticket-status-drop="onTicketStatusDrop"
            />
            <tickets-list-view
                v-else
                :tickets="listaItems"
                :loading="loadingtable"
                :server-items-length="listaTotal"
                :items-per-page="numberOfRows"
                :items-per-page-last-option="itemsPerPageLastOption"
                :server-sort-by="serverSortBy"
                :server-sort-desc="serverSortDesc"
                :status-prefer-dispatcher="dataSource === 'dispatcher'"
                :show-print="showPrintButton"
                @update:options="onTableOptions"
                @update:filters="onFiltersUpdate"
                @row-click="openDetail($event)"
                @print-ticket="openPrint"
            />
        </div>
        <ticket-detail-drawer
            ref="ticketDetailDrawer"
            v-model="drawerOpen"
            :ticket="selectedTicket"
            :ticket-id="selectedTicket && selectedTicket.id_ticket"
            :token="token"
            :user="user"
            :data-source="dataSource"
            :status="statusListForSelect"
            :visible-detail-fields="visibleDetailFields"
            :edit-checklist="editChecklist"
            :show-close-ticket-button="showCloseTicketButton"
            :show-conclude-ticket-button="showCloseTicketButton && dataSource === 'dispatcher'"
            :hide-internal-observation="hideInternalObservation"
            :pode-ver-historico="podeVerHistorico"
            :hide-precandidatos-write-actions="hidePrecandidatosWriteActions"
            :show-print-button="showPrintButton"
            :can-edit-schedule="canEditSchedule"
            :show-save-button="!isProfileAgenciaEmprego && !pendenteSomenteLeitura"
            :has-pending-changes="!!detailPendingPatch"
            :read-only="pendenteSomenteLeitura"
            :saving="detailSaving"
            @save="saveDetailChange"
            @discard-changes="discardDetailChanges"
            @print-ticket="openPrint"
            @request-close-ticket="$emit('request-close-ticket', $event)"
            @update:status="onStatusChange"
            @update:statusDispatcher="onStatusDispatcherChange"
            @update:statusAtribuido="onStatusAtribuidoChange"
            @update:team="onTeamChange"
            @update:schedule="onScheduleChange"
            @update:expectedFinalization="onExpectedFinalizationChange"
            @update:slaManual="onSlaManualChange"
            @update:user="onUserChange"
            @update:tipoTicket="onTipoTicketChange"
            @update:motivoStatus="onMotivoStatusChange"
            @update:familia="onFamiliaChange"
            @update:tipoAtendimento="onTipoAtendimentoChange"
            @update:servico="onServicoChange"
            @update:tipoSolicitacao="onTipoSolicitacaoChange"
            @update:moduloSistema="onModuloSistemaChange"
            @update:visualizadores="onVisualizadoresChange"
            @vehicle-equipment-pairs-change="onVehicleEquipmentPairsChange"
            @updated="onDetailUpdated"
            @open-checklist-dialog="$emit('open-checklist-dialog', $event)"
        />
        <ticket-print-view
            v-if="showPrintButton"
            v-model="printDialog"
            :ticket-id="printTicketId"
        />
    </div>
</template>

<script>
import apiController from '@/utils/apiController';
import EventBus from '../../utils/eventBus';
import { filterTicketStatusesByPerfil } from '@/utils/ticketStatusPerfilFilter';
import { itemIsAssignedToUser } from '@/utils/ticketAssignees';
import { ticketEstaPendente } from '@/utils/ticketPendente';
import { transicaoPermitidaNoQuadro } from '@/utils/ticketFluxoSuporte';
import CadastroToolbar from '@/components/Toolbars/CadastroToolbar.vue';
import serverTableMixin from '@/mixins/serverTableMixin';
import { buildRequestParams, computePageState } from '@/utils/serverTablePagination';
import TicketsViewToolbar, {
    normalizeTicketFieldFilters,
    defaultTicketFieldFiltersLastTwoLocalDays
} from './TicketsViewToolbar.vue';
import TicketsBoard from './TicketsBoard.vue';
import TicketsListView from './TicketsListView.vue';
import TicketDetailDrawer from './TicketDetailDrawer.vue';
import TicketPrintView from './TicketPrintView.vue';

/** Must match `tipos_tickets.nome_tipo_ticket` for agency candidate requests (cliente board uses id_status_atribuido first). */
const AGENCY_SOLICITACAO_CANDIDATOS_TIPO_TEXTO = 'SOLICITAÇÃO DE CANDIDATOS (AGÊNCIA)';
/** Cards por coluna do quadro a cada página (rolagem infinita) e linhas por página da lista. */
const TICKETS_POR_PAGINA = 20;
/** Para alterar a quantidade de colunas exportadas no CSV, altere este número. */
const CSV_EXPORT_STATUS_COLUMNS = 3;
/** Espera da digitação na busca antes de consultar o servidor. */
const BUSCA_DEBOUNCE_MS = 300;

export default {
    name: 'TicketsJiraView',
    mixins: [serverTableMixin],
    components: {
        CadastroToolbar,
        TicketsViewToolbar,
        TicketsBoard,
        TicketsListView,
        TicketDetailDrawer,
        TicketPrintView
    },
    props: {
        token: { type: String, required: true },
        user: { type: Object, default: null },
        dataSource: { type: String, default: 'cliente' },
        /** When dataSource is 'dispatcher': 'supervisor' = all tickets, 'dispatcher' = team tickets only */
        role: { type: String, default: '' },
        showNewTicket: { type: Boolean, default: true },
        title: { type: String, default: '' },
        /** When false, board cards are not draggable (status cannot be changed by drag-and-drop). */
        enableBoardStatusDrag: { type: Boolean, default: true },
        editChecklist: { type: Boolean, default: false },
        /** When true, detail toolbar shows cancelar ticket; on dispatcher, concluir is also shown (parent handles request-close-ticket). */
        showCloseTicketButton: { type: Boolean, default: false },
        /** Hide internal observation fields in ticket detail (cliente / criados). */
        hideInternalObservation: { type: Boolean, default: false },
        podeVerPendentes: { type: Boolean, default: false },
        podeVerHistorico: { type: Boolean, default: false },
        hidePrecandidatosWriteActions: { type: Boolean, default: false },
        /** Ícone de impressora na lista, nos cards e no detalhe; abre a pré-visualização de impressão do ticket. */
        showPrintButton: { type: Boolean, default: false },
        /** When true (default), team filter starts with the user's teams; when false, all teams are selected (no restriction). */
        prefilterUserTeams: { type: Boolean, default: true },
        /** Edição de data/hora de agendamento no detalhe; null segue ticket_campos_detalhe (schedule_edit). */
        canEditSchedule: { type: Boolean, default: null }
    },
    data() {
        return {
            /** Quadro: cards carregados por coluna (chave = id_status_ticket) e total filtrado de cada coluna. */
            ticketsPorStatus: {},
            totaisPorStatus: {},
            carregandoColuna: {},
            /** Opções das autocompletes da toolbar, vindas do servidor. */
            opcoesFiltro: { clientes: [], titulos: [], agencias: [], tiposSolicitacao: [], usuariosAtribuidos: [] },
            /** Lista: página atual (serverTableMixin). */
            listaItems: [],
            listaTotal: 0,
            loadingtable: false,
            pageNumber: 1,
            numberOfRows: TICKETS_POR_PAGINA,
            itemsPerPageLastOption: 1000,
            lastItemsPerPageBeforeTodos: null,
            serverSortBy: [],
            serverSortDesc: [],
            searchFilters: {},
            statusList: [],
            statusListForSelect: [],
            loading: false,
            viewMode: 'board',
            filters: {
                search: '',
                statusId: null,
                clientId: null,
                teamIds: [],
                fieldFilters: defaultTicketFieldFiltersLastTwoLocalDays()
            },
            equipeList: [],
            userTeamIds: [],
            teamFilterReady: false,
            selectedTicket: null,
            drawerOpen: false,
            printDialog: false,
            printTicketId: null,
            detailSaving: false,
            detailPendingPatch: null,
            /** Board: per status column, reverse card order when true (key = String(id_status_ticket)) */
            boardColumnCardsInverted: {},
            boardDropSaving: false
        };
    },
    computed: {
        /** Solicitante abrindo o próprio ticket Pendente: acompanha, mas quem faz a triagem é o editor. */
        pendenteSomenteLeitura() {
            return !this.podeVerPendentes && ticketEstaPendente(this.selectedTicket);
        },
        /** Screen (tela) for ticket_campos_detalhe: which fields to show in the detail dialog */
        detailScreen() {
            if (this.dataSource === 'dispatcher') {
                return this.role === 'supervisor' ? 'supervisor' : 'dispatcher';
            }
            return this.dataSource || 'cliente';
        },
        /** Array of campo names from ticket_campos_detalhe for the current screen (active only) */
        visibleDetailFields() {
            const list = this.$store && this.$store.state.config && this.$store.state.config.ticketCamposDetalhe;
            if (!list || list.length === 0) return null;
            return this.$store.getters['config/ticketCamposDetalheForScreen'](this.detailScreen);
        },
        /** Distinct clients present in the loaded ticket list (for toolbar filter). */
        defaultTeamIds() {
            const all = (this.equipeList || []).map((e) => Number(e.id_equipe)).filter((n) => Number.isFinite(n));
            if (!this.prefilterUserTeams) {
                return all;
            }
            const user = (this.userTeamIds || []).map((id) => Number(id)).filter((n) => Number.isFinite(n));
            return user.length > 0 ? user : all;
        },
        clientFilterOptions() {
            return (this.opcoesFiltro && this.opcoesFiltro.clientes) || [];
        },
        /** Tickets carregados na tela (colunas do quadro ou página da lista). */
        tickets() {
            if (this.viewMode !== 'board') return this.listaItems || [];
            return Object.keys(this.ticketsPorStatus || {}).reduce(
                (acc, sid) => acc.concat(this.ticketsPorStatus[sid] || []),
                []
            );
        },
        isProfileAgenciaEmprego() {
            return !!(this.user && this.user.perfil_usuario === 'AgenciaEmprego');
        },
        /** Drag-and-drop status change on the board. */
        boardStatusDragEnabled() {
            if (!this.enableBoardStatusDrag) return false;
            if (this.isProfileAgenciaEmprego) return false;
            return !!(this.token && this.user && this.user.id_usuario && !this.boardDropSaving);
        },
        csvExportTitle() {
            return this.title || 'tickets-jira';
        },
        /** Total do CSV: soma das colunas exportadas, conforme os filtros. */
        exportTotal() {
            return this.boardOrderedStatusIds()
                .slice(0, CSV_EXPORT_STATUS_COLUMNS)
                .reduce((n, sid) => n + (Number(this.totaisPorStatus[sid]) || 0), 0);
        }
    },
    watch: {
        user: { immediate: true, handler() { this.loadData(); } },
        dataSource() { this.loadData(); },
        role() { this.loadData(); },
        viewMode() {
            if (this.statusList.length) this.loadTickets({ reset: true });
        },
        clientFilterOptions(opts) {
            const list = opts || [];
            if (list.length === 0 && this.filters.clientId != null && this.filters.clientId !== '') {
                this.filters = { ...this.filters, clientId: null };
                return;
            }
            if (this.filters.clientId == null || this.filters.clientId === '') return;
            const ok = list.some(c => Number(c.id_cliente) === Number(this.filters.clientId));
            if (!ok) {
                this.filters = { ...this.filters, clientId: null };
            }
        }
    },
    mounted() {
        // A primeira carga vem do watcher `user` (immediate); chamar loadData aqui duplicaria as consultas.
        this.$store.dispatch('config/fetchTicketCamposDetalhe');
    },
    methods: {
        /** Ensures nested fieldFilters is always present so list filtering stays in sync with the toolbar. */
        onToolbarFiltersUpdate(ev) {
            if (!ev || typeof ev !== 'object') return;
            const anterior = JSON.stringify(this.filters);
            const buscaAnterior = this.filters && this.filters.search;
            this.filters = {
                search: ev.search != null ? String(ev.search) : '',
                statusId: ev.statusId != null && ev.statusId !== '' ? ev.statusId : null,
                clientId: ev.clientId != null && ev.clientId !== '' ? ev.clientId : null,
                teamIds: Array.isArray(ev.teamIds) ? ev.teamIds.slice() : this.defaultTeamIds.slice(),
                fieldFilters: normalizeTicketFieldFilters(ev.fieldFilters)
            };
            if (JSON.stringify(this.filters) === anterior) return;
            this.agendarRecarga(this.filters.search !== buscaAnterior ? BUSCA_DEBOUNCE_MS : 0);
        },
        /** Filtros mudaram: volta à primeira página (debounce só para a digitação da busca). */
        agendarRecarga(delay) {
            if (this.recargaTimer) clearTimeout(this.recargaTimer);
            this.recargaTimer = setTimeout(() => {
                this.recargaTimer = null;
                if (this.statusList.length) this.loadTickets({ reset: true });
            }, delay);
        },
        async loadData() {
            if (!this.token || !this.user || !this.user.id_usuario) return;
            this.loading = true;
            // Colunas (status) e equipes padrão definem a primeira consulta: carrega antes dos tickets.
            await Promise.all([this.loadStatus(), this.loadTeams()]);
            await this.loadTickets({ reset: true });
        },
        async loadTeams() {
            const uid = this.user.id_usuario;
            try {
                const [allRes, userRes] = await Promise.all([
                    apiController.request(apiController.endpoints.getallEquipes, { silent: true }),
                    apiController.request(apiController.endpoints.getEquipesporusuario, { params: { id_usuario: uid }, silent: true })
                ]);
                const all = Array.isArray(allRes.data) ? allRes.data : [];
                this.equipeList = all.filter((e) => e.ativo === 1 || e.ativo_extenso === 'Sim');
                this.userTeamIds = (Array.isArray(userRes.data) ? userRes.data : []).map((e) => e.id_equipe);
                if (!this.teamFilterReady) {
                    this.teamFilterReady = true;
                    this.filters = { ...this.filters, teamIds: this.defaultTeamIds.slice() };
                }
            } catch (e) {
                this.equipeList = [];
                this.userTeamIds = [];
            }
        },
        async loadStatus() {
            const screenForBoard = this.detailScreen || this.dataSource || 'board';
            const screenForSelect =
                this.dataSource === 'colaborador'
                    ? 'colaborador'
                    : (this.dataSource === 'dispatcher' ? 'dispatcher' : screenForBoard);
            try {
                const [allRes, forScreenRes] = await Promise.all([
                    apiController.request(apiController.endpoints.getallStatus, { silent: true }),
                    apiController.request(apiController.endpoints.getallStatus, { query: { screen: screenForSelect }, silent: true })
                ]);
                const baseAll = Array.isArray(allRes.data) ? allRes.data : [];
                const baseFor = Array.isArray(forScreenRes.data) ? forScreenRes.data : [];
                this.statusList = filterTicketStatusesByPerfil(baseAll, 'colunas');
                this.statusListForSelect = filterTicketStatusesByPerfil(baseFor, 'select');
            } catch (e) {
                EventBus.$emit('show-error', { error: e });
            }
        },
        /** Filtros da toolbar no formato do getticketsquadro (aplicados e contados no servidor). */
        filtrosRequisicao() {
            const f = this.filters || {};
            const talent = (this.equipeList || []).find(
                (x) => String(x.nome_equipe || '').trim() === 'Talent Acquisition'
            );
            return {
                search: f.search || '',
                statusId: f.statusId != null && f.statusId !== '' ? f.statusId : null,
                clientId: f.clientId != null && f.clientId !== '' ? f.clientId : null,
                teamIds: f.teamIds || [],
                allTeamIds: (this.equipeList || []).map((e) => Number(e.id_equipe)).filter((n) => Number.isFinite(n)),
                talentAcquisitionId: talent && talent.id_equipe != null ? Number(talent.id_equipe) : null,
                fieldFilters: normalizeTicketFieldFilters(f.fieldFilters)
            };
        },
        requisicaoQuadro(extra) {
            return apiController.request(apiController.endpoints.getTicketsquadro, {
                data: {
                    dataSource: this.dataSource,
                    role: this.role,
                    filtros: this.filtrosRequisicao(),
                    ...extra
                },
                silent: true
            });
        },
        colunaInvertida(statusId) {
            return (this.boardColumnCardsInverted || {})[String(statusId)] === true;
        },
        aplicarResumo(data) {
            this.totaisPorStatus = (data.contagens && data.contagens.porStatus) || {};
            if (data.opcoes) this.opcoesFiltro = data.opcoes;
        },
        /**
         * Recarrega o quadro ou a página da lista.
         * reset: volta para a primeira página de cada coluna; sem reset (após salvar), mantém quantos
         * cards cada coluna já mostrava para não perder a posição de rolagem.
         */
        async loadTickets({ reset = false } = {}) {
            if (this.viewMode !== 'board') {
                if (reset) this.pageNumber = 1;
                return this.serverTableLoad();
            }
            const geracao = (this.quadroGeracao = (this.quadroGeracao || 0) + 1);
            const colunas = {};
            this.boardOrderedStatusIds().forEach((sid) => {
                const carregados = reset ? 0 : (this.ticketsPorStatus[sid] || []).length;
                colunas[sid] = { offset: 0, limit: Math.max(TICKETS_POR_PAGINA, carregados), desc: this.colunaInvertida(sid) };
            });
            if (reset) this.loading = true;
            try {
                const res = await this.requisicaoQuadro({ modo: 'quadro', colunas });
                if (geracao !== this.quadroGeracao) return;
                const data = res.data || {};
                if (data.erro) {
                    EventBus.$emit('show-alert', { tipo: 'error', mensagem: data.mensagem });
                    return;
                }
                this.ticketsPorStatus = data.colunas || {};
                this.carregandoColuna = {};
                this.aplicarResumo(data);
                this.syncSelectedTicketFromList();
            } catch (e) {
                if (geracao !== this.quadroGeracao) return;
                this.ticketsPorStatus = {};
                EventBus.$emit('show-error', { error: e });
            } finally {
                if (geracao === this.quadroGeracao) this.loading = false;
            }
        },
        /** Rolagem infinita: próxima página de uma coluna do quadro. */
        async carregarMaisColuna(statusId) {
            const sid = Number(statusId);
            const atuais = this.ticketsPorStatus[sid] || [];
            if (this.carregandoColuna[sid] || atuais.length >= (Number(this.totaisPorStatus[sid]) || 0)) return;
            const geracao = this.quadroGeracao;
            this.carregandoColuna = { ...this.carregandoColuna, [sid]: true };
            try {
                const res = await this.requisicaoQuadro({
                    modo: 'quadro',
                    colunas: { [sid]: { offset: atuais.length, limit: TICKETS_POR_PAGINA, desc: this.colunaInvertida(sid) } }
                });
                // Um recarregamento completo (filtro, salvar) chegou antes: esta página ficou obsoleta.
                if (geracao !== this.quadroGeracao) return;
                const data = res.data || {};
                if (data.erro) return;
                const vistos = new Set((this.ticketsPorStatus[sid] || []).map((t) => t.id_ticket));
                const novos = ((data.colunas && data.colunas[sid]) || []).filter((t) => !vistos.has(t.id_ticket));
                this.ticketsPorStatus = { ...this.ticketsPorStatus, [sid]: (this.ticketsPorStatus[sid] || []).concat(novos) };
                this.aplicarResumo(data);
            } catch (e) {
                EventBus.$emit('show-error', { error: e });
            } finally {
                if (geracao === this.quadroGeracao) {
                    this.carregandoColuna = { ...this.carregandoColuna, [sid]: false };
                }
            }
        },
        /** Inverter a ordem de uma coluna refaz a consulta dela na nova ordem (a ordem vale para todas as páginas). */
        async onColumnCardsInvertedUpdate(next) {
            const prev = this.boardColumnCardsInverted || {};
            this.boardColumnCardsInverted = next;
            const mudou = Object.keys({ ...prev, ...next }).filter((k) => !!prev[k] !== !!next[k]);
            if (!mudou.length) return;
            const geracao = this.quadroGeracao;
            const colunas = {};
            mudou.forEach((sid) => {
                colunas[sid] = { offset: 0, limit: TICKETS_POR_PAGINA, desc: this.colunaInvertida(sid) };
            });
            try {
                const res = await this.requisicaoQuadro({ modo: 'quadro', colunas });
                if (geracao !== this.quadroGeracao) return;
                const data = res.data || {};
                if (data.erro) return;
                const atualizado = { ...this.ticketsPorStatus };
                mudou.forEach((sid) => { atualizado[sid] = (data.colunas && data.colunas[sid]) || []; });
                this.ticketsPorStatus = atualizado;
                this.aplicarResumo(data);
            } catch (e) {
                EventBus.$emit('show-error', { error: e });
            }
        },
        /** Lista paginada no servidor (serverTableMixin chama em paginação/ordenação/filtro de coluna). */
        async serverTableLoad(options) {
            const state = {
                pageNumber: this.pageNumber,
                numberOfRows: this.numberOfRows,
                serverSortBy: this.serverSortBy,
                serverSortDesc: this.serverSortDesc,
                searchFilters: this.searchFilters,
                lastItemsPerPageBeforeTodos: this.lastItemsPerPageBeforeTodos,
                total: this.listaTotal
            };
            const { params, page, rowsPerPage, sortByArr, sortDescArr } = buildRequestParams(options, state);
            const requestId = (this.listaRequestId = (this.listaRequestId || 0) + 1);
            this.loadingtable = true;
            try {
                const res = await this.requisicaoQuadro({ modo: 'lista', ...params });
                if (requestId !== this.listaRequestId) return;
                const data = res.data || {};
                if (data.erro) {
                    EventBus.$emit('show-alert', { tipo: 'error', mensagem: data.mensagem });
                    return;
                }
                this.listaItems = Array.isArray(data.items) ? data.items : [];
                this.aplicarResumo(data);
                const updates = computePageState(
                    { items: this.listaItems, total: Number(data.total) || 0 },
                    { page, rowsPerPage, sortByArr, sortDescArr },
                    state,
                    this.itemsPerPageLastOption
                );
                this.listaTotal = updates.total;
                this.pageNumber = updates.pageNumber;
                this.numberOfRows = updates.numberOfRows;
                this.lastItemsPerPageBeforeTodos = updates.lastItemsPerPageBeforeTodos;
                this.serverSortBy = updates.serverSortBy;
                this.serverSortDesc = updates.serverSortDesc;
                this.syncSelectedTicketFromList();
            } catch (e) {
                if (requestId === this.listaRequestId) EventBus.$emit('show-error', { error: e });
            } finally {
                if (requestId === this.listaRequestId) {
                    this.loadingtable = false;
                    this.loading = false;
                }
            }
        },
        /** CSV: busca no servidor as 3 primeiras colunas do quadro inteiras (com os filtros atuais). */
        async loadItemsForExport() {
            const colunas = {};
            this.boardOrderedStatusIds().slice(0, CSV_EXPORT_STATUS_COLUMNS).forEach((sid) => {
                colunas[sid] = { offset: 0, limit: 10000, desc: this.colunaInvertida(sid) };
            });
            const res = await this.requisicaoQuadro({ modo: 'quadro', colunas, semComentarios: true });
            const data = (res && res.data) || {};
            return Object.keys(colunas).reduce((acc, sid) => acc.concat((data.colunas && data.colunas[sid]) || []), []);
        },
        clearComentarioNovoFlag(id_ticket) {
            if (id_ticket == null) return;
            const tid = Number(id_ticket);
            const limpar = (list) => (list || []).map((t) => {
                if (Number(t.id_ticket) !== tid) return t;
                if (!t.tem_comentario_novo) return t;
                return { ...t, tem_comentario_novo: false };
            });
            const colunas = {};
            Object.keys(this.ticketsPorStatus || {}).forEach((sid) => { colunas[sid] = limpar(this.ticketsPorStatus[sid]); });
            this.ticketsPorStatus = colunas;
            this.listaItems = limpar(this.listaItems);
            if (this.selectedTicket && Number(this.selectedTicket.id_ticket) === tid) {
                this.selectedTicket = { ...this.selectedTicket, tem_comentario_novo: false };
            }
        },
        openPrint(ticket) {
            if (!ticket || ticket.id_ticket == null) return;
            this.printTicketId = ticket.id_ticket;
            this.printDialog = true;
        },
        openDetail(ticket) {
            this.detailPendingPatch = null;
            this.selectedTicket = ticket;
            this.drawerOpen = true;
            if (ticket && ticket.id_ticket != null) {
                this.clearComentarioNovoFlag(ticket.id_ticket);
            }
            this.maybeAdvanceColaboradorStatusOnFirstDetailOpen(ticket);
        },
        /** Colaborador: first time the assignee opens the detail, move ticket to board column 3 (server + migration on ticket_usuarios_atribuidos). */
        async maybeAdvanceColaboradorStatusOnFirstDetailOpen(ticket) {
            if (this.dataSource !== 'colaborador' || !this.token || !this.user || !this.user.id_usuario) return;
            if (!ticket || ticket.id_ticket == null) return;
            if (!itemIsAssignedToUser(ticket, this.user.id_usuario)) return;
            const tid = Number(ticket.id_ticket);
            if (!Number.isFinite(tid)) return;
            try {
                const res = await apiController.request(apiController.endpoints.colaboradorDetailfirstview, { data: { id_ticket: tid }, silent: true });
                if (!res.data || res.data.erro || !res.data.advanced) return;
                await this.loadTickets();
                this.syncSelectedTicketFromList();
                this.$nextTick(() => {
                    const d = this.$refs.ticketDetailDrawer;
                    if (d && typeof d.loadFullDetail === 'function') {
                        d.loadFullDetail();
                    }
                });
            } catch (e) {
                // falha silenciosa — avanço automático é best-effort
            }
        },
        patchForBoardStatusDrop(newStatusId) {
            const n = Number(newStatusId);
            if (!Number.isFinite(n)) return null;
            return { id_status_dispatcher: n, id_status_atribuido: n };
        },
        /** Same column order as TicketsBoard.orderedStatuses (ordem, then id). */
        boardOrderedStatusIds() {
            const list = (this.statusList || []).slice().sort((a, b) => {
                const aOrder = a.ordem != null ? Number(a.ordem) : Number(a.id_status_ticket || 0);
                const bOrder = b.ordem != null ? Number(b.ordem) : Number(b.id_status_ticket || 0);
                if (aOrder === bOrder) return Number(a.id_status_ticket || 0) - Number(b.id_status_ticket || 0);
                return aOrder - bOrder;
            });
            return list
                .map((s) => Number(s.id_status_ticket))
                .filter((id) => Number.isFinite(id));
        },
        boardEffectiveStatusId(ticket) {
            if (!ticket) return NaN;
            if (ticket.id_status_atribuido != null) return Number(ticket.id_status_atribuido);
            if (ticket.id_status_dispatcher != null) return Number(ticket.id_status_dispatcher);
            return NaN;
        },
        /** Colaborador: moving from 2nd board column to 3rd requires the ticket detail drawer open for that card. */
        colaboradorBlocksSecondToThirdColumnDrop(ticket, newStatusId, ticketId) {
            if (this.dataSource !== 'colaborador') return false;
            const ids = this.boardOrderedStatusIds();
            if (ids.length < 3) return false;
            const current = this.boardEffectiveStatusId(ticket);
            const next = Number(newStatusId);
            if (!Number.isFinite(current) || !Number.isFinite(next)) return false;
            const fromIdx = ids.indexOf(current);
            const toIdx = ids.indexOf(next);
            if (fromIdx !== 1 || toIdx !== 2) return false;
            const openForThisTicket =
                this.drawerOpen &&
                this.selectedTicket &&
                Number(this.selectedTicket.id_ticket) === Number(ticketId);
            return !openForThisTicket;
        },
        isTicketSuporte(ticket) {
            return String((ticket && ticket.tipo_ticket_extenso) || '').trim().toUpperCase() === 'SUPORTE SISTEMA (MERUSTRAKER)';
        },
        dropPermitidoNoSuporte(ticket, newStatusId) {
            const chave = (id) => {
                const s = (this.statusList || []).find((r) => Number(r.id_status_ticket) === Number(id));
                return s ? s.chave_i18n : null;
            };
            return transicaoPermitidaNoQuadro(chave(ticket.id_status_dispatcher), chave(newStatusId));
        },
        async onTicketStatusDrop({ id_ticket, id_status_ticket }) {
            if (this.isProfileAgenciaEmprego) return;
            if (this.boardDropSaving || !this.token) return;
            const tid = Number(id_ticket);
            const newSid = Number(id_status_ticket);
            if (!Number.isFinite(tid) || !Number.isFinite(newSid)) return;
            const ticket = (this.tickets || []).find(t => Number(t.id_ticket) === tid);
            if (!ticket) return;
            if (ticketEstaPendente(ticket)) {
                EventBus.$emit('show-alert', { tipo: 'warning', mensagem: this.$t('ticket.pendente.dragBlocked') });
                return;
            }
            if (this.isTicketSuporte(ticket) && !this.dropPermitidoNoSuporte(ticket, newSid)) {
                EventBus.$emit('show-alert', { tipo: 'warning', mensagem: this.$t('ticketSuporteFluxo.transicaoBloqueada') });
                return;
            }
            if (this.colaboradorBlocksSecondToThirdColumnDrop(ticket, newSid, tid)) {
                EventBus.$emit('show-alert', {
                    tipo: 'warning',
                    mensagem:
                        this.$t('ticket.alerts.warning.openDetailToAdvanceBoard') ||
                        'Abra o detalhe do ticket para movê-lo para a próxima coluna.'
                });
                return;
            }
            const d = ticket.id_status_dispatcher != null ? Number(ticket.id_status_dispatcher) : NaN;
            const a = ticket.id_status_atribuido != null ? Number(ticket.id_status_atribuido) : NaN;
            if (Number.isFinite(d) && d === newSid && Number.isFinite(a) && a === newSid) return;
            const patch = this.patchForBoardStatusDrop(newSid);
            if (!patch) return;
            const payload = {
                ...ticket,
                ...patch,
                id_ticket: tid
            };
            this.boardDropSaving = true;
            try {
                const response = await apiController.request(apiController.endpoints.updateTicket, { data: payload, silent: true });
                if (response.data && response.data.erro) {
                    EventBus.$emit('show-alert', {
                        tipo: 'error',
                        mensagem: response.data.mensagem || (this.$t('ticket.alerts.error.status') || 'Erro ao atualizar o ticket.')
                    });
                    return;
                }
                EventBus.$emit('show-alert', {
                    tipo: 'success',
                    mensagem: this.$t('ticket.alerts.success.status') || 'Status atualizado.'
                });
                await this.loadTickets();
                this.syncSelectedTicketFromList();
                this.$nextTick(() => {
                    const d = this.$refs.ticketDetailDrawer;
                    if (d && typeof d.loadFullDetail === 'function') {
                        d.loadFullDetail();
                    }
                });
            } catch (e) {
                EventBus.$emit('show-error', { error: e });
            } finally {
                this.boardDropSaving = false;
            }
        },
        onDetailUpdated() {
            this.loadTickets().then(() => {
                this.$nextTick(() => {
                    if (this.$refs.ticketDetailDrawer && typeof this.$refs.ticketDetailDrawer.loadFullDetail === 'function') {
                        this.$refs.ticketDetailDrawer.loadFullDetail();
                    }
                });
            });
        },
        /** Colaborador: after ChecklistFulfillmentDialog save, refresh open drawer and open change-status dialog */
        onChecklistSavedFromParent(ticket) {
            if (this.dataSource !== 'colaborador' || !ticket || !ticket.id_ticket) return;
            if (!this.drawerOpen || !this.selectedTicket || Number(this.selectedTicket.id_ticket) !== Number(ticket.id_ticket)) return;
            const d = this.$refs.ticketDetailDrawer;
            if (!d) return;
            if (typeof d.requestOpenChangeStatusWhenDetailLoaded === 'function') {
                d.requestOpenChangeStatusWhenDetailLoaded();
            }
            if (typeof d.loadFullDetail === 'function') {
                d.loadFullDetail();
            }
        },
        syncSelectedTicketFromList() {
            if (!this.selectedTicket || !this.selectedTicket.id_ticket) return;
            const updated = (this.tickets || []).find(t => t.id_ticket === this.selectedTicket.id_ticket);
            if (updated) {
                this.selectedTicket = { ...updated };
            }
        },
        /** Acumula a edição no ticket aberto; só é gravada no botão Salvar do detalhe. */
        stageDetailChange(patch) {
            if (this.isProfileAgenciaEmprego) return;
            if (!patch || !this.selectedTicket || !this.selectedTicket.id_ticket) return;
            this.selectedTicket = {
                ...(this.selectedTicket || {}),
                ...patch
            };
            this.detailPendingPatch = { ...(this.detailPendingPatch || {}), ...patch };
        },
        discardDetailChanges() {
            this.detailPendingPatch = null;
            this.syncSelectedTicketFromList();
        },
        async saveDetailChange() {
            if (this.isProfileAgenciaEmprego) {
                this.detailPendingPatch = null;
                return;
            }
            if (this.detailSaving) return;
            if (!this.selectedTicket || !this.selectedTicket.id_ticket || !this.detailPendingPatch) return;
            const payload = {
                ...this.selectedTicket,
                ...this.detailPendingPatch,
                id_ticket: this.selectedTicket.id_ticket
            };
            if (payload.dt_prevista_fim != null) {
                payload.dt_prevista_fim = this.formatDateToMySQL(payload.dt_prevista_fim);
            }
            if (payload.dt_agendamento != null) {
                payload.dt_agendamento = this.formatDateToMySQL(payload.dt_agendamento);
            }
            if (payload.dt_inicio != null) {
                payload.dt_inicio = this.formatDateToMySQL(payload.dt_inicio);
            }
            if (payload.dt_fim != null) {
                payload.dt_fim = this.formatDateToMySQL(payload.dt_fim);
            }
            this.detailSaving = true;
            try {
                const response = await apiController.request(apiController.endpoints.updateTicket, { data: payload, silent: true });
                if (response.data && response.data.erro) {
                    EventBus.$emit('show-alert', {
                        tipo: 'error',
                        mensagem: response.data.mensagem || (this.$t('ticket.alerts.error.status') || 'Erro ao atualizar o ticket.')
                    });
                    return;
                }
                const camposPendentes = Array.isArray(response.data && response.data.campos_pendentes)
                    ? response.data.campos_pendentes
                    : [];
                EventBus.$emit('show-alert', camposPendentes.length
                    ? { tipo: 'warning', mensagem: this.$t('ticket.pendente.savedStillPending') }
                    : { tipo: 'success', mensagem: this.$t('ticket.alerts.success.updated') });
                // Salvou: fecha o card e atualiza o quadro.
                this.detailPendingPatch = null;
                this.drawerOpen = false;
                this.selectedTicket = null;
                await this.loadTickets();
            } catch (e) {
                EventBus.$emit('show-error', { error: e });
            } finally {
                this.detailSaving = false;
            }
        },
        onStatusChange(value) {
            this.stageDetailChange({ id_status: value });
        },
        isSolicitacaoCandidatosAgenciaTicket(ticket) {
            return !!(ticket && String(ticket.tipo_ticket_extenso || '').trim() === AGENCY_SOLICITACAO_CANDIDATOS_TIPO_TEXTO);
        },
        onStatusDispatcherChange(value) {
            const patch = { id_status_dispatcher: value };
            if (this.dataSource === 'dispatcher' && this.isSolicitacaoCandidatosAgenciaTicket(this.selectedTicket)) {
                patch.id_status_atribuido = value;
            }
            this.stageDetailChange(patch);
        },
        /** O diálogo de status da tarefa já tem confirmação própria: grava na hora (com o que estiver pendente). */
        onStatusAtribuidoChange(value) {
            this.stageDetailChange(value || {});
            this.saveDetailChange();
        },
        onTeamChange(value) {
            const id = value != null && value !== '' ? Number(value) : null;
            if (id != null && Number.isFinite(id)) {
                this.stageDetailChange({ id_equipe: id, id_equipes: [id] });
            } else {
                this.stageDetailChange({ id_equipe: null, id_equipes: [] });
            }
        },
        onScheduleChange(value) {
            this.stageDetailChange({ dt_agendamento: value });
        },
        onExpectedFinalizationChange(value) {
            this.stageDetailChange({ dt_prevista_fim: value });
        },
        onSlaManualChange(value) {
            this.stageDetailChange({ sla_manual: value || null });
        },
        /** A atribuição de técnico já tem confirmação própria: grava na hora (com o que estiver pendente). */
        onUserChange(value) {
            this.stageDetailChange(value || {});
            this.saveDetailChange();
        },
        /** O tipo do ticket abre a cascata do cadastro: o que depende dele é escolhido de novo. */
        onTipoTicketChange(value) {
            this.stageDetailChange({
                id_tipo_ticket: value,
                id_tipo_solicitacao: null,
                id_familia_atendimento: null,
                id_tipo_atendimento: null,
                id_servico: null,
                id_checklist_equipamento: null,
                nome_checklist_extenso: null
            });
        },
        onMotivoStatusChange(value) {
            this.stageDetailChange({ motivo_status: value || null });
        },
        onFamiliaChange(value) {
            const patch = {
                id_familia_atendimento: value,
                id_tipo_atendimento: null,
                id_servico: null,
                id_checklist_equipamento: null,
                nome_checklist_extenso: null
            };
            // A família de atendimento define o grupo solucionador (como fazia a criação).
            const drawer = this.$refs.ticketDetailDrawer;
            const familias = drawer && Array.isArray(drawer.familiasAtendimento) ? drawer.familiasAtendimento : [];
            const familia = familias.find(f => Number(f.id_familia_atendimento) === Number(value));
            if (familia && familia.id_equipe != null) {
                patch.id_equipe = Number(familia.id_equipe);
                patch.id_equipes = [Number(familia.id_equipe)];
            }
            if (this.isTicketSuporte(this.selectedTicket) && !(familia && familia.chave_i18n === 'familia.software')) {
                patch.id_modulo_sistema = null;
            }
            this.stageDetailChange(patch);
        },
        onTipoAtendimentoChange(value) {
            this.stageDetailChange({
                id_tipo_atendimento: value,
                id_servico: null,
                id_checklist_equipamento: null,
                nome_checklist_extenso: null
            });
        },
        onServicoChange(id_servico) {
            this.stageDetailChange({ id_servico });
        },
        onTipoSolicitacaoChange(value) {
            this.stageDetailChange({ id_tipo_solicitacao: value != null && value !== '' ? value : null });
        },
        onModuloSistemaChange(value) {
            this.stageDetailChange({ id_modulo_sistema: value != null && value !== '' ? value : null });
        },
        onVisualizadoresChange(ids) {
            this.stageDetailChange({ id_usuarios_visualizadores: Array.isArray(ids) ? ids : [] });
        },
        /** Build updateticket association fields from pairs (same rules as the former ticket edit flow). */
        buildVehicleEquipmentPatch(pairs, kits) {
            const allEquipamentos = [];
            const allKits = [];
            const allTiposEquipamento = [];
            const allTiposKits = [];
            const allVeiculos = [];
            const seenEquipamentos = new Set();
            const seenKits = new Set();
            const seenTiposEquipamento = new Set();
            const seenTiposKits = new Set();
            const seenVeiculos = new Set();
            (pairs || []).forEach(pair => {
                if (pair.id_veiculo && !seenVeiculos.has(pair.id_veiculo)) {
                    allVeiculos.push(pair.id_veiculo);
                    seenVeiculos.add(pair.id_veiculo);
                }
                (pair.equipamentos || []).forEach(equipamentoId => {
                    const idStr = String(equipamentoId);
                    if (idStr.startsWith('te_')) {
                        const id = parseInt(idStr.replace('te_', ''), 10);
                        if (!isNaN(id) && !seenTiposEquipamento.has(id)) {
                            allTiposEquipamento.push(id);
                            seenTiposEquipamento.add(id);
                        }
                        return;
                    }
                    if (idStr.startsWith('tk_')) {
                        const id = parseInt(idStr.replace('tk_', ''), 10);
                        if (!isNaN(id) && !seenTiposKits.has(id)) {
                            allTiposKits.push(id);
                            seenTiposKits.add(id);
                        }
                        return;
                    }
                    const kit = (kits || []).find(k => k.id_kit === equipamentoId);
                    if (kit) {
                        if (!seenKits.has(kit.id_kit)) {
                            allKits.push({ id_kit: kit.id_kit, id_tipo_kit: kit.id_tipo_kit });
                            seenKits.add(kit.id_kit);
                        }
                    } else if (!seenEquipamentos.has(equipamentoId)) {
                        allEquipamentos.push(equipamentoId);
                        seenEquipamentos.add(equipamentoId);
                    }
                });
            });
            return {
                id_equipamentos: allEquipamentos.length > 0 ? allEquipamentos : [],
                id_kits: allKits.length > 0 ? allKits : [],
                id_tipos_equipamento: allTiposEquipamento.length > 0 ? allTiposEquipamento : [],
                id_tipos_kits: allTiposKits.length > 0 ? allTiposKits : [],
                id_veiculos: allVeiculos.length > 0 ? allVeiculos : []
            };
        },
        onVehicleEquipmentPairsChange(pairs) {
            if (!this.selectedTicket || !this.selectedTicket.id_ticket) return;
            const drawer = this.$refs.ticketDetailDrawer;
            const kits = drawer && Array.isArray(drawer.kits) ? drawer.kits : [];
            const patch = this.buildVehicleEquipmentPatch(pairs, kits);
            this.stageDetailChange(patch);
        },
        formatDateToMySQL(dateValue) {
            if (!dateValue) return null;
            if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(dateValue)) {
                return dateValue;
            }
            const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
            if (isNaN(date.getTime())) return null;
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const seconds = String(date.getSeconds()).padStart(2, '0');
            return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
        }
    }
};
</script>

<style scoped>
.tickets-jira-view {
    width: 100%;
    background-color: #fff;
}
.board-list-area {
    height: auto;
    max-height: 90vh;
    min-height: 200px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    padding-left: 10px;
    padding-right: 10px;
}
</style>
